// Get API base URL
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5000/api';
  }
  
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return `${protocol}//${hostname}/api`;
  }
  
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();
const MOBILE_ADMIN_KEY = 'x0vg5fgdfvDfv45';

// Types
export interface TeamMember {
  id: number;
  name: string;
  specialization: string;
}

export interface Appointment {
  id: number;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  date: string;
  time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  notes: string | null;
  totalPrice: number | null;
  serviceIds: number[];
  serviceNames: string[];
  teamMemberId: number | null;
  teamMember: TeamMember | null;
  customDuration: number | null;
  createdAt: string;
}

export interface AppointmentsResponse {
  success: boolean;
  date: string;
  teamMemberId: number | null;
  count: number;
  appointments: Appointment[];
}

export interface TeamMembersResponse {
  success: boolean;
  count: number;
  teamMembers: TeamMember[];
}

export interface ConflictInfo {
  id: number;
  customerName: string;
  customerPhone: string;
  time: string;
}

export interface ConfirmResponse {
  success: boolean;
  conflict?: boolean;
  message: string;
  conflicts?: ConflictInfo[];
  appointment?: {
    id: number;
    customerName: string;
    date: string;
    time: string;
    teamMember: string;
    status?: string;
  };
}

export interface ApiError {
  success: false;
  message: string;
  errors?: any[];
}

// Session storage cache
const CACHE_KEY_PREFIX = 'mobile_admin_';
const CACHE_DURATION = 30000; // 30 seconds

interface CacheData<T> {
  data: T;
  timestamp: number;
}

const getFromCache = <T>(key: string): T | null => {
  try {
    const cached = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${key}`);
    if (!cached) return null;

    const { data, timestamp }: CacheData<T> = JSON.parse(cached);
    const now = Date.now();

    if (now - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${key}`);
      return null;
    }

    return data;
  } catch {
    return null;
  }
};

const saveToCache = <T>(key: string, data: T): void => {
  try {
    const cacheData: CacheData<T> = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}${key}`, JSON.stringify(cacheData));
  } catch {
    // Ignore cache errors
  }
};

const clearCache = (): void => {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch {
    // Ignore cache errors
  }
};

// Fetch helper with retry logic
async function fetchWithRetry<T>(
  url: string,
  options: RequestInit = {},
  retries = 2
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error: any = new Error(errorData.message || 'Request failed');
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    return await response.json();
  } catch (error: any) {
    if (retries > 0 && (error.name === 'TypeError' || error.status >= 500)) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      return fetchWithRetry<T>(url, options, retries - 1);
    }
    throw error;
  }
}

// API functions
export const mobileAdminApi = {
  /**
   * Get team members for filtering
   */
  async getTeamMembers(): Promise<TeamMembersResponse> {
    const cacheKey = 'team_members';
    
    const cached = getFromCache<TeamMembersResponse>(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const url = `${API_BASE_URL}/mobile-admin/${MOBILE_ADMIN_KEY}/team-members`;
      
      const response = await fetchWithRetry<TeamMembersResponse>(url);
      
      saveToCache(cacheKey, response);
      return response;
    } catch (error: any) {
      throw new Error(
        error.data?.message || 
        error.message ||
        'Failed to fetch team members.'
      );
    }
  },

  /**
   * Get appointments for a specific date
   * @param date - Date in YYYY-MM-DD format (required)
   * @param teamMemberId - Team member ID for filtering (optional)
   * @param useCache - Whether to use cached data (default: true)
   */
  async getAppointments(
    date: string,
    teamMemberId?: number | null,
    useCache: boolean = true
  ): Promise<AppointmentsResponse> {
    const cacheKey = `appointments_${date}_${teamMemberId || 'all'}`;

    if (useCache) {
      const cached = getFromCache<AppointmentsResponse>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    try {
      let params = `?date=${date}`;
      if (teamMemberId) {
        params += `&teamMemberId=${teamMemberId}`;
      }
      
      const url = `${API_BASE_URL}/mobile-admin/${MOBILE_ADMIN_KEY}/appointments${params}`;
      
      const response = await fetchWithRetry<AppointmentsResponse>(url);
      
      saveToCache(cacheKey, response);
      return response;
    } catch (error: any) {
      throw new Error(
        error.data?.message || 
        error.message ||
        'Failed to fetch appointments. Please check your connection.'
      );
    }
  },

  /**
   * Confirm an appointment
   * @param id - Appointment ID
   * @param forceConfirm - Force confirmation even if there's a conflict
   */
  async confirmAppointment(id: number, forceConfirm: boolean = false): Promise<ConfirmResponse> {
    try {
      const url = `${API_BASE_URL}/mobile-admin/${MOBILE_ADMIN_KEY}/appointments/${id}/confirm`;
      
      const response = await fetchWithRetry<ConfirmResponse>(url, {
        method: 'POST',
        body: JSON.stringify({ forceConfirm }),
      });
      
      // Clear cache on successful confirmation
      clearCache();
      
      return response;
    } catch (error: any) {
      // Check if it's a conflict error (409)
      if (error.status === 409 && error.data) {
        return error.data;
      }
      
      throw new Error(
        error.data?.message || 
        error.message ||
        'Failed to confirm appointment. Please try again.'
      );
    }
  },

  /**
   * Reject/cancel an appointment
   * @param id - Appointment ID
   */
  async rejectAppointment(id: number): Promise<ConfirmResponse> {
    try {
      const url = `${API_BASE_URL}/mobile-admin/${MOBILE_ADMIN_KEY}/appointments/${id}/reject`;
      
      const response = await fetchWithRetry<ConfirmResponse>(url, {
        method: 'POST',
      });
      
      // Clear cache on successful rejection
      clearCache();
      
      return response;
    } catch (error: any) {
      throw new Error(
        error.data?.message || 
        error.message ||
        'Failed to cancel appointment. Please try again.'
      );
    }
  },

  /**
   * Clear all cached data
   */
  clearCache,
};

export default mobileAdminApi;
